import { rem } from 'polished'
import { useCallback, useMemo } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'

import TextButton from 'LuxKit/Button/TextButton'
import LabeledSheet from 'LuxKit/LabeledSheet'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import OfferInclusionsList from 'components/Common/Inclusions/OfferInclusionsList'
import SuggestedOptionPreviewRoomImage from 'components/OfferPage/SuggestedOptionPreviewRoomImage'
import SuggestedPackagePromotionLabel from 'components/OfferPage/SuggestedPackagePromotionLabel'
import { trackOfferPageSuggestedPackageViewPackageClick } from 'components/OfferPage/analytics'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Group from 'components/utils/Group'
import { mediaQueryDown } from 'components/utils/breakpoint'
import { ChertoExperiments } from 'constants/experiments'
import { NO_IMAGE_AVAILABLE_ID } from 'constants/image'
import type { OfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import useExperiment from 'hooks/Experiments/useExperiment'
import { getPopularForOccupantsBannerMessage } from 'hooks/OfferPage/useSuggestedPackageOptionFromTotalSales'
import { preventRenderWithOpenFilter } from 'lib/offer/offerPageUtil'
import { scrollToId } from 'lib/window/scrollUtils'
import type { CheapestRecommendedRoomAndRates } from 'selectors/offerPage/propertyOfferPageSelectors'
import {
  getCheapestRecommendedRoomAndRates,
  hasCheapestPromotionRate,
  hasLoadedRatesForDatesAndOccupancies,
} from 'selectors/offerPage/propertyOfferPageSelectors'

import PropertyCancellationPolicy from './PropertyCancellationPolicy'
import PropertyCheapestRatePriceDetails from './PropertyCheapestRatePriceDetails'

const INCLUSIONS_SHOWN = 2

const Body = styled(Group)`
  padding: ${rem(24)} ${rem(20)};
`

const ContentContainer = styled.div`
  flex: 1;
  min-width: 0;
  container-type: inline-size;
`

const Content = styled.div`
  display: flex;
  gap: ${rem(20)};
  flex-direction: column;

  @container (min-width: 410px) {
    flex-direction: row;
    align-items: start;
    justify-content: space-between;
  }

  ${mediaQueryDown.mobile} {
    flex-direction: column;
    align-items: stretch;
  }
`

const DesktopImageContainer = styled.div`
  width: ${rem(120)};
  height: ${rem(120)};
  flex-shrink: 0;
  border-radius: ${props => props.theme.borderRadius.M};
  overflow: hidden;

  ${mediaQueryDown.mobile} {
    display: none;
  }
`

const MobileRoomImageWrap = styled.div`
  width: ${rem(64)};
  height: ${rem(64)};
  border-radius: ${props => props.theme.borderRadius.M};
  overflow: hidden;
`

const DetailsColumn = styled.div`
  flex: 1;
  min-width: 0;
`

const PricingColumn = styled(Group)`
  width: ${rem(170)};
  flex-shrink: 0;

  ${mediaQueryDown.mobile} {
    width: 100%;
    align-self: stretch;
  }
`

interface Props {
  property: App.Property.Property
  occupancies: Array<OfferPageRoom>
  cheapestRecommendedRoomAndRates: CheapestRecommendedRoomAndRates
  hasCheapestPromotionRate: boolean
  hasLoadedRates: boolean
}

function PropertyRecommendedRateSummary({
  property,
  occupancies,
  cheapestRecommendedRoomAndRates,
  hasCheapestPromotionRate,
  hasLoadedRates,
}: Props) {
  const intl = useIntl()
  useExperiment(ChertoExperiments.sppRecommendedRateInheritance, {
    canActivate: hasLoadedRates && occupancies.length === 1,
  })

  const [hotelOnlyRoomAndRate, hotelWithFlightsRoomAndRate] = cheapestRecommendedRoomAndRates
  const room = hotelOnlyRoomAndRate?.[0]
  const hotelOnlyRate = hotelOnlyRoomAndRate?.[1]

  const strongCalloutMessage = useMemo(() => {
    return getPopularForOccupantsBannerMessage(intl, occupancies)
  }, [intl, occupancies])
  const allInclusions = useMemo<Array<App.OfferInclusion>>(() => hotelOnlyRate
    ? [
        ...hotelOnlyRate.luxPlusInclusions,
        ...hotelOnlyRate.bonusInclusions,
        ...hotelOnlyRate.bundleInclusions,
        ...hotelOnlyRate.inclusions,
      ]
    : [],
  [hotelOnlyRate])
  const showMoreInclusionsText = intl.formatMessage(
    {
      id: 'eiaPF_tSKjiIYr_Q2Xk-',
      defaultMessage: '+{count} more inclusions',
      description: 'Link text showing how many additional inclusions are truncated on the rebrand suggested option preview tile',
    },
    { count: Math.max(0, allInclusions.length - INCLUSIONS_SHOWN) },
  )
  const scrollToRoom = useCallback(() => {
    if (!room) return
    scrollToId(room.id)
  }, [room])
  const viewPackage = useCallback(() => {
    trackOfferPageSuggestedPackageViewPackageClick()
    scrollToRoom()
  }, [scrollToRoom])

  // Let `<PropertyPromotionSummary/>` render instead.
  if (hasCheapestPromotionRate) return null

  if (!(room && hotelOnlyRate && strongCalloutMessage)) return null

  const roomImageId = room.images[0]?.id ?? NO_IMAGE_AVAILABLE_ID
  // This is a quick heuristic to ensure the cheapest hotel-with-flights rate has the
  // same inclusions as the cheapest hotel-only rate.
  const hotelWithFlightsRate = hotelWithFlightsRoomAndRate?.[1].rateId === hotelOnlyRate.rateId
    ? hotelWithFlightsRoomAndRate[1]
    : undefined

  return <LabeledSheet
    kind="highlight"
    label={<SuggestedPackagePromotionLabel message={strongCalloutMessage} />}
  >
    <Body direction="horizontal" horizontalAlign="space-between" verticalAlign="start" gap={20}>
      <DesktopImageContainer>
        <SuggestedOptionPreviewRoomImage
          id={roomImageId}
          width={120}
          height={120}
          mobileWidth="120px"
        />
      </DesktopImageContainer>
      <ContentContainer>
        <Content>
          <DetailsColumn>
            <Group direction="vertical" gap={4}>
              <CSSBreakpoint max="mobile">
                <Group direction="horizontal" gap={12} verticalAlign="center">
                  <MobileRoomImageWrap>
                    <SuggestedOptionPreviewRoomImage
                      id={roomImageId}
                      width={64}
                      height={64}
                      mobileWidth="64px"
                    />
                  </MobileRoomImageWrap>
                  <Heading variant="heading4">{room.name}</Heading>
                </Group>
              </CSSBreakpoint>
              <CSSBreakpoint min="tablet">
                <BodyText variant="large" weight="bold">{room.name}</BodyText>
              </CSSBreakpoint>
              <PropertyCancellationPolicy
                rate={hotelOnlyRate}
                propertyTimeZone={property.location.timezone}
                shouldClarifyRefundIsHotelOnly={!!hotelWithFlightsRate}
              />
              {allInclusions.length > 0 && <OfferInclusionsList
                inclusions={allInclusions}
                truncateAfter={INCLUSIONS_SHOWN}
                onShowMoreClick={scrollToRoom}
                showMoreText={showMoreInclusionsText}
              />}
            </Group>
          </DetailsColumn>
          <PricingColumn direction="vertical" gap={16} verticalAlign="start">
            <PropertyCheapestRatePriceDetails
              hotelOnlyRate={hotelOnlyRate}
              hotelWithFlightsRate={undefined}
              horizontalAlign="start"
              size="M"
              hideLoyaltyPointsEarn
            />
            <TextButton
              kind="secondary"
              fit="mobile-full-width"
              onClick={viewPackage}
            >
              {intl.formatMessage({
                id: '7z4-Ngi2aAhJS1Y3B-TV',
                defaultMessage: 'View package',
                description: 'Button text to view package details in suggested option preview',
              })}
            </TextButton>
          </PricingColumn>
        </Content>
      </ContentContainer>
    </Body>
  </LabeledSheet>
}

type MappedProps = Props & { open: boolean }

export default withOfferPageAndReduxState((state, appState): MappedProps => ({
  property: state.property!, // Guaranteed by `<PropertyContainer/>`.
  occupancies: state.rooms,
  cheapestRecommendedRoomAndRates: getCheapestRecommendedRoomAndRates(state, appState),
  hasCheapestPromotionRate: hasCheapestPromotionRate(state, appState),
  hasLoadedRates: hasLoadedRatesForDatesAndOccupancies(state, appState),
  open: !!state.activeFilter,
}), PropertyRecommendedRateSummary, preventRenderWithOpenFilter)
